import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import { ReactNode } from 'react'
import ga from 'src/utils/pixels/gtag'

/** Surfaces that mount an FAQ accordion. Adding a new one? Register it here
 *  AND in the PostHog `faq_expand` insight breakdown. Keep the literal stable
 *  — it's the slice key on every analytics dashboard. */
export type FAQSurface =
  | 'seguro'
  | 'disputas'
  | 'reembolso'
  | 'anfitrion'
  | 'flotillas'
  | 'tradicional'
  | 'estacionamiento'
  | 'bodegas'
  | 'negocio'
  | 'invite'
  | 'invite-user'
  | 'on-demand'
  | 'size-guide'
  | 'landing'
  | 'personalized'
  | 'search'
  | 'logistics'
  | 'logistics-host'
  | 'developers'

interface IProps {
  title: string
  description: string
  index: number
  defaultExpanded?: boolean
  /** Required — identifies which page mounted the accordion so PostHog's
   *  `faq_expand` breakdown stays clean. Never default inline; new callers
   *  must pick the matching `FAQSurface` literal. */
  surface: FAQSurface
  /** Optional rendered body. When provided, replaces the plain-text
   *  `description` paragraph — used by entries that need inline links
   *  (e.g. the on-demand FAQ entry). The plain-text `description` is
   *  still required so it can feed the FAQPageJsonLd schema. */
  descriptionNode?: ReactNode
}

const FAQAccordion = ({
  title,
  description,
  index,
  defaultExpanded = false,
  surface,
  descriptionNode
}: IProps) => {
  // Fire only on expansion — collapse would 2× the event volume without
  // signal. defaultExpanded items don't fire on mount (only on user-driven
  // re-expand) since the change handler captures user intent, not initial
  // render state.
  const handleChange = (_: unknown, expanded: boolean) => {
    if (!expanded) return
    ga.event('faq_expand', { surface, index })
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      onChange={handleChange}
      disableGutters
      sx={{
        boxShadow: 'none',
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: '1px solid rgba(18, 38, 63, 0.12)',
        borderRadius: 0,
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-expandIconWrapper': {
          transition: 'transform 200ms ease'
        },
        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
          transform: 'none'
        },
        '& .icon-open': { display: 'flex' },
        '& .icon-close': { display: 'none' },
        '&.Mui-expanded .icon-open': { display: 'none' },
        '&.Mui-expanded .icon-close': { display: 'flex' }
      }}
    >
      <AccordionSummary
        sx={{
          padding: '28px 0',
          '& .MuiAccordionSummary-content': {
            margin: 0,
            gap: '24px',
            alignItems: 'center'
          }
        }}
        expandIcon={
          <span className="flex size-10 items-center justify-center rounded-full border border-ink-100/30 text-ink-700 transition-colors group-hover:border-blue-500 group-hover:bg-blue-500 group-hover:text-white-500">
            <AddRoundedIcon className="icon-open text-xl" />
            <RemoveRoundedIcon className="icon-close text-xl" />
          </span>
        }
        aria-controls={`panel-FAQ-${index}-content`}
        id={`panel-FAQ-${index}-header`}
      >
        <span
          aria-hidden="true"
          className="font-display text-lg text-blue-500 italic md:text-xl"
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <h4 className="text-base font-medium text-ink-700 md:text-lg">
          {title}
        </h4>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0 0 28px 56px' }}>
        {descriptionNode ? (
          <div className="max-w-2xl text-sm leading-relaxed text-ink-200 md:text-base md:leading-[1.7]">
            {descriptionNode}
          </div>
        ) : (
          <p className="max-w-2xl text-sm leading-relaxed text-ink-200 md:text-base md:leading-[1.7]">
            {description}
          </p>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default FAQAccordion
